import { type ReactElement } from 'react';
import { Button, Dialog, Stack, Text } from '@citibox-panel/ui';
import { formatCount } from '../lib/coverage';

export interface BatchConfirmDialogProps {
  open: boolean;
  intent: 'enable' | 'disable';
  count: number;
  // The BARE market label — «ES», «UK», «Sandbox», «Dev» — never the brand pill's `Soporte ES`,
  // which would render «…en Soporte ES». `null` is a deployment that cannot be trusted to name its
  // market at all; the host resolves both.
  marketLabel: string | null;
  isWriting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

// The panel cannot check that an id exists — `GET /{id}` never 404s — so pre-validation is
// impossible, not merely expensive. Saying so is the whole mitigation.
const EXISTENCE_CAVEAT = 'El panel no puede comprobar que los IDs existan.';
const UNKNOWN_MARKET = 'No se puede confirmar el mercado de este despliegue.';

// The last thing between a pasted column and thousands of buildings. It names the two facts that
// make stopping worth it — how many, and WHICH market — because the write is irreversible, reports
// nothing per item, and each deployment reaches only its own market's app-service.
//
// TODO: i18n — copy en ES hasta Fase 1.
export function BatchConfirmDialog({
  open,
  intent,
  count,
  marketLabel,
  isWriting,
  onConfirm,
  onCancel,
}: BatchConfirmDialogProps): ReactElement {
  const isEnable = intent === 'enable';
  const verb = isEnable ? 'activar' : 'desactivar';
  // No «en …» clause at all when the market is unknown: a dash where the market should be reads as
  // a market, and this is the one write whose irreversible failure is being in the wrong one.
  const market = marketLabel === null ? '' : ` en ${marketLabel}`;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      label={`Confirmar ${verb} ubicaciones`}
      maxWidth="xs"
      actions={
        <>
          {/* autoFocus on the way OUT: a modal over an irreversible act must not open pre-armed. */}
          <Button variant="outlined" color="neutral" autoFocus onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            color={isEnable ? 'primary' : 'error'}
            loading={isWriting}
            onClick={onConfirm}
          >
            {isEnable ? 'Activar' : 'Desactivar'}
          </Button>
        </>
      }
    >
      <Stack spacing={1}>
        <Text variant="subtitle1">
          {`Vas a ${verb} ${formatCount(count)} ubicaciones${market}`}
        </Text>
        {marketLabel === null && (
          <Text variant="body2" color="error">
            {UNKNOWN_MARKET}
          </Text>
        )}
        <Text variant="body2">{EXISTENCE_CAVEAT}</Text>
      </Stack>
    </Dialog>
  );
}
